import { useGetCurrentAgentsPresenter, useGetLanguageByVoiceID } from '@/pages/agents/hooks';
import { isTalkPresenter } from '@/pages/agents/utils';
import { INITIAL_VALUES } from '@/state/agent';
import { useAgentEditorStore } from '@/state/root';
import { Presenter } from '@d-id/types';
import equal from 'fast-deep-equal';
import { toJS } from 'mobx';

export function useCompareEditStates() {
    const agentEditorStore = useAgentEditorStore();

    const presenter: Presenter | undefined = useGetCurrentAgentsPresenter();
    const agent = agentEditorStore.inputAgent;
    const languageByVoiceID = useGetLanguageByVoiceID(agent?.presenter?.voice?.voice_id);
    const selectedLanguage = agent?.presenter?.voice?.language || languageByVoiceID;

    return {
        getIsAgentStateChanged: () => {
            const {
                ttsSelectedVoice,
                presenterIsStitch,
                inputAgent: { preview_name },
                uploadingDocumentFiles,
                starterQuestions,
                baseKnowledge,
                greetings,
                initAgentState,
                presenterBackgroundSourceUrl,
                presenterBackgroundColor,
                llm,
                triggers,
            } = agentEditorStore;
            const localState = {
                presenter_id: presenter?.id,
                preview_name: preview_name?.trim(),
                ...(initAgentState.knowledgeId && {
                    knowledgeId: initAgentState.knowledgeId,
                }),
                ...(ttsSelectedVoice?.id && {
                    language: ttsSelectedVoice.language,
                    voiceId: ttsSelectedVoice.id,
                }),
                ...(initAgentState.documents && { documents: uploadingDocumentFiles }),
                ...(starterQuestions && {
                    starter_message: starterQuestions.toString(),
                }),
                ...(baseKnowledge && {
                    base_knowledge: baseKnowledge,
                }),
                ...(greetings?.length && greetings[0] && { greetings: greetings[0] }),
                ...(isTalkPresenter(agent?.presenter) && {
                    presenter_stitch: presenterIsStitch,
                    presenter_background: presenterBackgroundSourceUrl,
                }),
                ...(presenterBackgroundColor && {
                    presenter_background_color: presenterBackgroundColor,
                }),
                llm: toJS(llm),
                triggers: agent?.triggers,
            };

            const initState = {
                ...initAgentState,
                preview_name: agent?.preview_name || '',
                ...(ttsSelectedVoice?.id && {
                    language: selectedLanguage,
                    voiceId: agent?.presenter?.voice?.voice_id,
                }),
                ...(initAgentState.documents && { documents: initAgentState.documents }),
                ...(starterQuestions && {
                    starter_message: starterQuestions.toString(),
                }),
                ...(baseKnowledge && {
                    base_knowledge: baseKnowledge,
                }),
                ...(agent?.greetings?.length && agent.greetings[0] && { greetings: agent?.greetings[0] }),
                ...(isTalkPresenter(agent?.presenter) && {
                    presenter_stitch: agent?.presenter.stitch,
                    presenter_background: INITIAL_VALUES.presenterBackgroundSourceUrl,
                }),
                ...(agent?.presenter?.background?.color && {
                    presenter_background_color: agent?.presenter?.background?.color,
                }),
                llm: agent?.llm,
                triggers: toJS(triggers) || {},
            };

            return !equal(localState, initState);
        },
    };
}
